import { MouseEvent, useState } from 'react';
import {
  Button,
  Box,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import ImageIdeaCard from './ImageIdeaCard';
import PdfIdeaCard from './PdfIdeaCard';
import VideoIdeaCard from './VideoIdeaCard';
import TextIdeaCard from './TextIdeaCard';
import IdeaCardVotes from './IdeaCardVotes';
import IdeaCardEditor from './IdeaCardEditor';
import IdeaCardComments from './IdeaCardComments';

import useBoardStore from '../../store/boardStore';
import useUserStore from '../../store/userStore';
import { dotvotingApi } from '../../services/dotvotingApi';
import { rotateImageBlob } from '../../utils/rotateImage';

export interface IdeaCardProps {
  ideaId: string;
}

const hasIdeaContent = (value: string) =>
  /<img\b[^>]*>/i.test(value) ||
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

const IdeaCard = ({ ideaId }: IdeaCardProps) => {
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editorValue, setEditorValue] = useState('');
  const [imageRotation, setImageRotation] = useState(0);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const idea = useBoardStore((state) => state.ideas.find((candidate) => candidate.id === ideaId));
  const togglePinned = useBoardStore((state) => state.togglePinned);
  const updateIdea = useBoardStore((state) => state.updateIdea);
  const updateImageIdea = useBoardStore((state) => state.updateImageIdea);
  const removeIdea = useBoardStore((state) => state.removeIdea);
  const approveIdea = useBoardStore((state) => state.approveIdea);
  const setViewCard = useBoardStore((state) => state.setViewCard);
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const participants = useBoardStore((state) => state.participants);
  const currentUser = useUserStore((state) => state.getCurrentUser());

  if (!idea) {
    return null;
  }

  const isIdeaOwner = currentUser ? idea.ownerId === currentUser.id : false;
  const isBoardOwner = boardDetails?.isOwner === true;
  const canDeleteIdea = isIdeaOwner || isBoardOwner;
  const canPinIdea = isBoardOwner && boardDetails?.cardSorting !== 'custom';
  const commentCount = idea.comments.length;
  const canShowActions = canPinIdea || canDeleteIdea;
  const participantName =
    idea.ownerName?.trim() ??
    participants.find((participant) => participant.id === idea.ownerId)?.name?.trim() ??
    '';
  const canShowParticipantName =
    participantName.length > 0 &&
    Boolean(
      (boardDetails?.isOwner && boardDetails?.viewParticipantNames) ||
        boardDetails?.allViewParticipantNames
    );

  const hideVoteTotals = boardDetails?.privateVotingEnabled === true && !boardDetails?.isOwner;
  const hasVoting = boardDetails?.votingStyle !== 'none';
  const votingEnabled = boardDetails?.votingEnabled !== false;
  const commentsEnabled = boardDetails?.commentsEnabled !== false;
  const requiresApproval = boardDetails?.cardsRequireApproval === true;
  const isPendingApproval = requiresApproval && idea.approved === false;
  const canShowVotes =
    Boolean(boardDetails) && hasVoting && !isPendingApproval && (votingEnabled || !hideVoteTotals);
  const isEditableIdeaType = idea.type === 'text' || idea.type === 'image';
  const originalEditorValue = idea.type === 'image' ? idea.title : idea.content;
  const hasEditorChanges = editorValue !== originalEditorValue;
  const hasImageRotationChanges = idea.type === 'image' && imageRotation !== 0;

  const canSaveEdit =
    idea.type === 'text'
      ? hasIdeaContent(editorValue) && hasEditorChanges
      : hasEditorChanges || hasImageRotationChanges;
  const isPdfIdea =
    idea.type === 'file' &&
    (idea.mimeType === 'application/pdf' ||
      idea.title?.trim().toLowerCase().endsWith('.pdf') === true ||
      idea.content?.trim().toLowerCase().endsWith('.pdf') === true);

  const renderContent = () => {
    if (idea.type === 'image') {
      return <ImageIdeaCard idea={idea} showBackground={true} />;
    }

    if (idea.type === 'video') {
      return <VideoIdeaCard idea={idea} showBackground={true} />;
    }

    if (isPdfIdea) {
      return <PdfIdeaCard idea={idea} showBackground={true} />;
    }

    return <TextIdeaCard idea={idea} showBackground={true} />;
  };
  const openActions = (event: MouseEvent<HTMLElement>) => setActionsAnchorEl(event.currentTarget);
  const closeActions = () => setActionsAnchorEl(null);
  const handleCardClick = () => {
    if (isEditing) {
      return;
    }

    setViewCard(idea);
  };
  const startEditing = () => {
    setEditorValue(idea.type === 'image' ? idea.title : idea.content);
    setImageRotation(0);
    setIsEditing(true);
    closeActions();
  };
  const cancelEditing = () => {
    setIsEditing(false);
    setEditorValue(idea.type === 'image' ? idea.title : idea.content);
    setImageRotation(0);
  };
  const rotateImage = (offset: number) => {
    setImageRotation((currentRotation) => {
      const nextRotation = (currentRotation + offset) % 360;
      return nextRotation < 0 ? nextRotation + 360 : nextRotation;
    });
  };

  const saveEditing = async () => {
    if (!canSaveEdit || isSavingEdit || !isEditableIdeaType) {
      return;
    }

    setIsSavingEdit(true);
    try {
      if (idea.type === 'image' && hasImageRotationChanges) {
        const imageBlob = await dotvotingApi.getIdeaImageBlob(idea.topic, idea.id);
        const rotatedBlob = await rotateImageBlob(imageBlob, imageRotation);
        await updateImageIdea(ideaId, rotatedBlob, editorValue, rotatedBlob.type || idea.mimeType);
      } else {
        await updateIdea(
          ideaId,
          idea.type === 'image' ? { title: editorValue } : { content: editorValue }
        );
      }
      setIsEditing(false);
      setImageRotation(0);
    } finally {
      setIsSavingEdit(false);
    }
  };

  if (isEditing && isEditableIdeaType) {
    return (
      <IdeaCardEditor
        idea={idea}
        editorValue={editorValue}
        isSavingEdit={isSavingEdit}
        canSaveEdit={canSaveEdit}
        imageRotation={imageRotation}
        onEditorValueChange={setEditorValue}
        onRotateImage={rotateImage}
        onCancelEditing={cancelEditing}
        onSaveEditing={saveEditing}
        onCardClick={handleCardClick}
      />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Card
        id={`idea-body_${idea.id}`}
        elevation={0}
        sx={(theme) => ({
          position: 'relative',
          cursor: isEditing ? 'default' : 'pointer',
          transition: theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.shortest,
          }),
          boxShadow: theme.shadows[1],
          '&:hover': {
            boxShadow: theme.shadows[4],
          },
        })}
        onClick={handleCardClick}
      >
        <CardContent sx={{ px: 1.5, py: canShowActions ? 0.5 : 2, '&:last-child': { pb: 1.5 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={canShowParticipantName ? 'space-between' : 'flex-end'}
            gap={1}
            mb={canShowParticipantName ? 0.25 : 0}
          >
            {canShowParticipantName && (
              <Typography variant="caption2">{participantName}</Typography>
            )}
            {canShowActions && (
              <Box display="flex" justifyContent="flex-end" alignItems="start" mb={0.5} mr={-0.5}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    openActions(event);
                  }}
                  aria-label="Show actions"
                  aria-controls="idea-card-actions"
                  aria-haspopup="true"
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                <Menu
                  id="idea-card-actions"
                  anchorEl={actionsAnchorEl}
                  open={Boolean(actionsAnchorEl)}
                  onClose={closeActions}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  {canPinIdea && (
                    <MenuItem
                      onClick={() => {
                        void togglePinned(ideaId);
                        closeActions();
                      }}
                    >
                      <PushPinIcon fontSize="small" sx={{ mr: 1 }} />
                      {idea.pinned ? 'Unpin' : 'Pin'}
                    </MenuItem>
                  )}
                  {isIdeaOwner && (
                    <MenuItem
                      onClick={() => {
                        startEditing();
                      }}
                      disabled={!isEditableIdeaType}
                    >
                      <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                      Edit
                    </MenuItem>
                  )}
                  {canDeleteIdea && (
                    <MenuItem
                      onClick={() => {
                        void removeIdea(ideaId);
                        closeActions();
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                      Delete
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            )}
          </Stack>
          <Box>{renderContent()}</Box>
          {!isEditing && (canShowVotes || commentsEnabled) && (
            <Stack
              mt={1.5}
              direction="row"
              spacing={1}
              alignItems={!hideVoteTotals ? 'flex-start' : 'center'}
              justifyContent={canShowVotes ? 'space-between' : 'flex-end'}
            >
              {canShowVotes && <IdeaCardVotes idea={idea} />}
              <IdeaCardComments
                ideaId={idea.id}
                commentsEnabled={commentsEnabled}
                commentCount={commentCount}
                comments={idea.comments}
                participants={participants}
              />
            </Stack>
          )}
          {isPendingApproval && (
            <Stack
              mt={1.25}
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              {boardDetails?.isOwner ? (
                <>
                  <Button
                    size="small"
                    variant="text"
                    onClick={(event) => {
                      event.stopPropagation();
                      void approveIdea(idea.id);
                    }}
                  >
                    Approve
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    |
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    onClick={(event) => {
                      event.stopPropagation();
                      void removeIdea(idea.id);
                    }}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Awaiting approval
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
      {boardDetails?.cardSorting !== 'custom' && idea.pinned && (
        <Box
          sx={{
            position: 'absolute',
            top: -16,
            left: -10,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            borderRadius: '50%',
            width: 28,
            height: 28,
            mt: 0.5,
            border: '1px solid',
            borderColor: 'divider',
          }}
          aria-label="Pinned idea"
        >
          <PushPinIcon sx={{ height: '16px', width: '16px' }} />
        </Box>
      )}
    </Box>
  );
};

export default IdeaCard;
